import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { PageHeader } from '../../components/ui/PageHeader';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

import { AutomationAccessModal } from './AutomationAccessModal';
import { InviteUserModal } from './InviteUserModal';

type Role = 'SUPER_ADMIN' | 'ADWEB_OPERATOR' | 'CLIENT_ADMIN' | 'CLIENT_USER';

interface UserRow {
  id: string;
  email: string;
  fullName: string;
  role: 'SUPER_ADMIN' | 'ADWEB_OPERATOR' | 'CLIENT_ADMIN' | 'CLIENT_USER';
  status: 'INVITED' | 'ACTIVE' | 'DISABLED';
  lastLoginAt: string | null;
  organizationId: string;
}

interface ListResponse {
  data: UserRow[];
  meta: { total: number };
}

async function fetchUsers(): Promise<ListResponse> {
  return (await apiClient.get<ListResponse>('/users?page=1&pageSize=100')).data;
}

export function UsersAdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState<UserRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const query = useQuery({ queryKey: ['users'], queryFn: fetchUsers });

  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  // Qui peut gérer les accès automatisations d'un utilisateur CLIENT_USER :
  //  – SUPER_ADMIN / ADWEB_OPERATOR : tous, toutes orgs
  //  – CLIENT_ADMIN : uniquement les utilisateurs de sa propre organisation
  const canManageAccessOf = (u: UserRow) => {
    if (u.role !== 'CLIENT_USER') return false; // seul ce rôle a un canTrigger filtré
    if (isPlatform) return true;
    if (user?.role === 'CLIENT_ADMIN' && u.organizationId === user.organizationId) return true;
    return false;
  };
  // Un CLIENT_ADMIN ne peut attribuer que des rôles clients ; seul un
  // SUPER_ADMIN peut attribuer SUPER_ADMIN.
  const assignableRoles: Role[] = isSuperAdmin
    ? ['SUPER_ADMIN', 'ADWEB_OPERATOR', 'CLIENT_ADMIN', 'CLIENT_USER']
    : isPlatform
      ? ['ADWEB_OPERATOR', 'CLIENT_ADMIN', 'CLIENT_USER']
      : ['CLIENT_ADMIN', 'CLIENT_USER'];

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) =>
      apiClient.patch(`/users/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) =>
      setActionError(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Modification impossible.',
      ),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => apiClient.post(`/users/${id}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) =>
      setActionError(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Désactivation impossible.',
      ),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => apiClient.post(`/users/${id}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) =>
      setActionError(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Réactivation impossible.',
      ),
  });

  function handleDeactivate(u: UserRow) {
    setActionError(null);
    if (window.confirm(`Désactiver le compte de ${u.fullName} (${u.email}) ?`)) {
      deactivate.mutate(u.id);
    }
  }

  function handleReactivate(u: UserRow) {
    setActionError(null);
    if (window.confirm(`Réactiver le compte de ${u.fullName} (${u.email}) ?`)) {
      reactivate.mutate(u.id);
    }
  }

  const canDelete = user?.role === 'SUPER_ADMIN';
  const del = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) =>
      setActionError(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Suppression impossible.',
      ),
  });

  function handleDelete(u: UserRow) {
    setActionError(null);
    if (
      window.confirm(
        `Supprimer définitivement le compte de ${u.fullName} (${u.email}) ?\n\n` +
          "L'historique (exécutions, incidents) est conservé mais l'attribution devient " +
          'anonyme. Action irréversible. Pour bloquer temporairement, préférez « Désactiver ».',
      )
    ) {
      del.mutate(u.id);
    }
  }

  // Affiché dans la colonne « Rôle », ou sous le nom sur téléphone.
  function roleControl(u: UserRow) {
    if (!assignableRoles.includes(u.role) || u.id === user?.id) return t(`users.roles.${u.role}`);
    return (
      <select
        value={u.role}
        onChange={(e) => changeRole.mutate({ id: u.id, role: e.target.value as Role })}
        disabled={changeRole.isPending}
        aria-label={`Rôle de ${u.fullName}`}
        className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs"
      >
        {assignableRoles.map((r) => (
          <option key={r} value={r}>
            {t(`users.roles.${r}`)}
          </option>
        ))}
      </select>
    );
  }

  const statusClass: Record<UserRow['status'], string> = {
    ACTIVE: 'bg-emerald-100 text-emerald-800',
    INVITED: 'bg-amber-100 text-amber-800',
    DISABLED: 'bg-slate-200 text-slate-600',
  };

  return (
    <>
      <PageHeader
        helpTopic="utilisateurs"
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        actions={
          <Button variant="inverse" onClick={() => setModalOpen(true)} size="sm">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Inviter un utilisateur
          </Button>
        }
      />
      <InviteUserModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <AutomationAccessModal
        open={!!accessTarget}
        onClose={() => setAccessTarget(null)}
        userId={accessTarget?.id ?? null}
        userName={accessTarget?.fullName ?? ''}
        userOrgId={accessTarget?.organizationId ?? ''}
      />
      <section className="px-4 sm:px-6 lg:px-8 py-6">
        {actionError && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {actionError}
          </div>
        )}
        {query.isLoading ? (
          <SkeletonTable rows={5} cols={6} />
        ) : query.isError ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : query.data && query.data.data.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t('users.empty')}
            action={
              <Button onClick={() => setModalOpen(true)} size="sm" className="mt-2">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Inviter un utilisateur
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto surface">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('users.fullName')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 md:table-cell">
                    {t('users.email')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 sm:table-cell">
                    {t('users.role')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 sm:table-cell">
                    {t('users.status')}
                  </th>
                  <th scope="col" className="hidden px-4 py-3 lg:table-cell">
                    {t('users.lastLogin')}
                  </th>
                  <th scope="col" className="px-4 py-3" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(query.data?.data ?? []).map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-2.5 text-slate-900">
                      {u.fullName}
                      {/* Petits écrans : l'e-mail passe sous le nom. */}
                      <div className="text-xs text-slate-500 md:hidden">{u.email}</div>
                      <div className="mt-1 text-xs text-slate-600 sm:hidden">{roleControl(u)}</div>
                    </td>
                    <td className="hidden px-4 py-2.5 text-slate-600 md:table-cell">{u.email}</td>
                    <td className="hidden px-4 py-2.5 text-slate-600 sm:table-cell">
                      {roleControl(u)}
                    </td>
                    <td className="hidden px-4 py-2.5 sm:table-cell">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusClass[u.status]}`}
                      >
                        {t(`users.statuses.${u.status}`)}
                      </span>
                    </td>
                    <td className="hidden px-4 py-2.5 text-slate-500 lg:table-cell">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        {canManageAccessOf(u) && (
                          <button
                            type="button"
                            onClick={() => setAccessTarget(u)}
                            className="text-xs font-medium text-brand hover:underline"
                            title="Choisir quelles automatisations cet utilisateur peut exécuter"
                          >
                            Accès
                          </button>
                        )}
                        {u.status !== 'DISABLED' &&
                          u.id !== user?.id &&
                          (u.role !== 'SUPER_ADMIN' || isSuperAdmin) && (
                            <button
                              type="button"
                              onClick={() => handleDeactivate(u)}
                              disabled={deactivate.isPending}
                              className="text-xs font-medium text-amber-600 hover:underline disabled:opacity-50"
                            >
                              Désactiver
                            </button>
                          )}
                        {u.status === 'DISABLED' && (u.role !== 'SUPER_ADMIN' || isSuperAdmin) && (
                          <button
                            type="button"
                            onClick={() => handleReactivate(u)}
                            disabled={reactivate.isPending}
                            className="text-xs font-medium text-emerald-600 hover:underline disabled:opacity-50"
                          >
                            Réactiver
                          </button>
                        )}
                        {canDelete && u.id !== user?.id && (
                          <button
                            type="button"
                            onClick={() => handleDelete(u)}
                            disabled={del.isPending}
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
